import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isClickedOutside, isMobileBreakpoint } from '@theme/utilities';
import { fetchFilteredRelatedProducts, getFirstAvailableVariant } from '@theme/product-main-collection-filter';
import { getDrawerSectionRenderUrl } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {Record<string, any[]>} */
  #relatedProductsCache = {};

  /** @type {MutationObserver | null} */
  #relatedSectionObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #relatedHydrateTimer = null;

  /** @type {Promise<void> | null} */
  #hotProductsPromise = null;

  connectedCallback() {
    super.connectedCallback();

    const nativeCloseDialog = this.closeDialog;
    this.closeDialog = async () => {
      if (this.hasAttribute('data-suppress-close')) return;
      return nativeCloseDialog.call(this);
    };

    this.addEventListener('click', this.#blockOutsideCloseWhileSuppressing, { capture: true });
    this.addEventListener('click', this.#blockOutsideCloseWhileSuppressing);

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#startRelatedSectionObserver);
    this.addEventListener(DialogCloseEvent.eventName, this.#stopRelatedSectionObserver);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    this.#openFromUrlParamIfPresent();
  }

  /** Opens drawer when URL has `?cart_drawer=open` (e.g. redirect from `/cart`), then cleans the query. */
  #openFromUrlParamIfPresent() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cart_drawer') !== 'open') return;

    params.delete('cart_drawer');
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
    history.replaceState(null, '', nextUrl);

    queueMicrotask(() => {
      try {
        this.open();
      } catch (e) {
        console.warn('[cart-drawer] open from URL param failed', e);
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#blockOutsideCloseWhileSuppressing, { capture: true });
    this.removeEventListener('click', this.#blockOutsideCloseWhileSuppressing);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#startRelatedSectionObserver);
    this.removeEventListener(DialogCloseEvent.eventName, this.#stopRelatedSectionObserver);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#stopRelatedSectionObserver();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #blockOutsideCloseWhileSuppressing = (event) => {
    if (!this.hasAttribute('data-suppress-close')) return;

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    if (isClickedOutside(event, dialog)) {
      event.stopImmediatePropagation();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');

    this.#hydrateRelatedProducts(dialog);
    this.#hydrateEmptyHotProducts(dialog);
  }

  #startRelatedSectionObserver = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    this.#hydrateRelatedProducts(dialog);
    this.#hydrateEmptyHotProducts(dialog);
    this.#stopRelatedSectionObserver();

    this.#relatedSectionObserver = new MutationObserver(() => {
      if (!dialog.open) return;
      if (this.hasAttribute('data-drawer-updating')) return;

      if (this.#relatedHydrateTimer) clearTimeout(this.#relatedHydrateTimer);
      this.#relatedHydrateTimer = setTimeout(() => {
        this.#relatedHydrateTimer = null;
        this.#hydrateRelatedProducts(dialog);
        this.#hydrateEmptyHotProducts(dialog);
      }, 150);
    });

    this.#relatedSectionObserver.observe(dialog, {
      childList: true,
      subtree: true,
    });
  };

  #stopRelatedSectionObserver = () => {
    if (this.#relatedHydrateTimer) clearTimeout(this.#relatedHydrateTimer);
    this.#relatedHydrateTimer = null;
    this.#relatedSectionObserver?.disconnect();
    this.#relatedSectionObserver = null;
  };

  /**
   * Loads empty-cart "What's Hot" product cards after the drawer opens
   * so unrelated product images are not present in the initial page HTML.
   * @param {HTMLElement} dialog
   */
  #hydrateEmptyHotProducts(dialog) {
    const carousels = dialog.querySelectorAll('[data-cart-hot-lazy]:not([data-cart-hot-loaded])');
    if (!carousels.length) return;

    if (this.#hotProductsPromise) {
      this.#hotProductsPromise.then(() => this.#hydrateEmptyHotProducts(dialog));
      return;
    }

    this.#hotProductsPromise = this.#loadEmptyHotProducts(dialog).finally(() => {
      this.#hotProductsPromise = null;
    });
  }

  /**
   * @param {HTMLElement} dialog
   */
  async #loadEmptyHotProducts(dialog) {
    const carousels = [...dialog.querySelectorAll('[data-cart-hot-lazy]:not([data-cart-hot-loaded])')];
    if (!carousels.length) return;

    /** @type {HTMLElement[]} */
    const needsFetch = [];

    carousels.forEach((carousel) => {
      if (!(carousel instanceof HTMLElement)) return;

      // Theme editor (and any pre-rendered cards) already have markup.
      if (carousel.querySelector('.cart-drawer__hot-card')) {
        carousel.setAttribute('data-cart-hot-loaded', '');
        carousel.setAttribute('aria-busy', 'false');
        return;
      }

      needsFetch.push(carousel);
    });

    if (!needsFetch.length) return;

    const sectionId = needsFetch[0]?.getAttribute('data-cart-hot-section') || 'cart-drawer-hot-products';
    const url = getDrawerSectionRenderUrl();
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Hot products section failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cardsRoot = doc.querySelector('[data-cart-hot-cards]');
      const cardsHtml = cardsRoot?.innerHTML?.trim() || '';

      needsFetch.forEach((carousel) => {
        if (carousel.hasAttribute('data-cart-hot-loaded')) return;

        if (cardsHtml) {
          carousel.innerHTML = cardsHtml;
        } else {
          carousel.closest('.cart-drawer__hot-section')?.setAttribute('hidden', '');
        }

        carousel.setAttribute('data-cart-hot-loaded', '');
        carousel.setAttribute('aria-busy', 'false');
      });
    } catch (error) {
      console.error('[cart-drawer] Failed to load hot products:', error);
      needsFetch.forEach((carousel) => {
        carousel.setAttribute('aria-busy', 'false');
      });
    }
  }

  /**
   * Replaces non-empty mini-cart "Frequently Bought Together" cards
   * with Shopify related recommendations for the first cart product.
   * @param {HTMLElement} dialog
   */
  async #hydrateRelatedProducts(dialog) {
    if (this.hasAttribute('data-drawer-updating')) return;

    const section = dialog.querySelector('.cart-drawer__hot-section--related');
    if (!(section instanceof HTMLElement)) return;

    const productId = section.dataset.relatedProductId;
    const limit = parseInt(section.dataset.relatedLimit || '5', 10);
    const mainCollectionHandle = section.dataset.relatedMainCollection || '';
    if (!productId) return;

    const currentHydrationKey = `${productId}:${limit}:${mainCollectionHandle}`;
    if (section.dataset.relatedHydratedFor === currentHydrationKey) return;

    const cards = section.querySelectorAll('.cart-drawer__hot-card');
    if (!cards.length) return;

    try {
      const products = await this.#fetchRelatedProducts(productId, limit, mainCollectionHandle);
      if (!products.length) return;

      cards.forEach((card, index) => {
        const product = products[index];
        if (!product) {
          card.remove();
          return;
        }

        this.#hydrateRelatedCard(card, product);
      });

      section.dataset.relatedHydratedFor = currentHydrationKey;
    } catch (error) {
      console.error('Failed to hydrate drawer recommendations:', error);
    }
  }

  /**
   * @param {string} productId
   * @param {number} limit
   * @param {string} mainCollectionHandle
   * @returns {Promise<any[]>}
   */
  async #fetchRelatedProducts(productId, limit, mainCollectionHandle = '') {
    const cacheKey = `${productId}:${limit}:${mainCollectionHandle || 'all'}`;
    if (this.#relatedProductsCache[cacheKey]) {
      return this.#relatedProductsCache[cacheKey];
    }

    const products = await fetchFilteredRelatedProducts(
      productId,
      limit,
      mainCollectionHandle,
      Theme?.routes?.product_recommendations_url || '/recommendations/products'
    );

    this.#relatedProductsCache[cacheKey] = products;
    return products;
  }

  /**
   * @param {Element} card
   * @param {any} product
   */
  #hydrateRelatedCard(card, product) {
    const productUrl = product.url || '#';
    const productTitle = product.title || '';

    // Keep existing card layout; only update content/targets from recommendations.
    card.querySelectorAll('a[href]').forEach((link) => {
      link.setAttribute('href', productUrl);
    });

    const image = card.querySelector('img');
    if (image instanceof HTMLImageElement && product.featured_image) {
      image.src = product.featured_image;
      image.srcset = '';
      image.alt = image.alt || productTitle;
    }

    const title = card.querySelector('.resource-card__title');
    if (title) {
      title.textContent = productTitle;
    }

    const priceEl = card.querySelector('.price');
    if (priceEl) {
      const amount = Number(product.price || 0);
      const formatted = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: Shopify?.currency?.active || 'GBP',
      }).format(amount / 100);
      priceEl.textContent = formatted;
    }

    const variantId = getFirstAvailableVariant(product)?.id;
    const variantInput = card.querySelector('.cart-drawer__hot-add-form input[name="id"]');
    if (variantInput instanceof HTMLInputElement && variantId) {
      variantInput.value = `${variantId}`;
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
